import type {
  InputHTMLAttributes,
  KeyboardEvent,
  ReactElement,
  ReactNode,
} from 'react'
import { useEffect, useRef } from 'react'

import type { CleaveOptions } from 'cleave.js/options'
import Cleave from 'cleave.js/react'
import { Controller, useFormContext } from 'react-hook-form'
import type { RegisterOptions, UseFormReturn } from 'react-hook-form'

import { FormHelperText } from '@twisto/components/atoms/form-helper-text'
import { styleUtils } from '@twisto/styles'
import { keyCodes } from '@twisto/utils'

import * as styles from './code-input.styles'

type Props = {
  children: (values: string[], greyLength: number) => ReactNode
  value?: string
  name: string
  error?: boolean
  helperText?: ReactElement | string | undefined
  formattedOptions?: CleaveOptions
  disableAutofocusOnNumberKeyPress?: boolean
  rules?: RegisterOptions
  required?: boolean
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'children'>

const minChars = 4

export const BaseCodeInput = ({
  children,
  value: valueProp,
  error: errorProp = false,
  helperText,
  className,
  minLength = minChars,
  required = false,
  maxLength,
  formattedOptions = {},
  name,
  type = 'text',
  autoFocus = false,
  disableAutofocusOnNumberKeyPress = false,
  rules = {},
  ...other
}: Props) => {
  const { formState, watch, register, control, setValue } =
    (useFormContext() as UseFormReturn | null) || {}
  const fieldErrors = formState?.errors ?? {}

  const hasError = errorProp || Boolean(fieldErrors[name])
  const value = watch ? (watch(name, '') as string) : valueProp ?? ''
  const unicodedValue = Array.from(value)
  const inputField = useRef<HTMLInputElement | null>(null)
  const totalLength = Math.min(
    Math.max(unicodedValue.length + 1, minLength),
    maxLength ?? minLength
  )

  register?.(name, {
    ...rules,
    required,
    minLength,
    maxLength,
  })

  // on mobile input maxlength can be exceeded, see https://stackoverflow.com/a/56186285
  const greyLength = Math.max(totalLength - unicodedValue.length, 0)

  const handleOnKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode === keyCodes.LEFTARROW || e.keyCode === keyCodes.RIGHTARROW) {
      e.preventDefault()
    }
  }

  useEffect(() => {
    const isNumberKeyPressed = (event: globalThis.KeyboardEvent) => {
      const isNumber = /^[0-9]$/i.test(event.key)

      if (isNumber && inputField.current) {
        inputField.current.focus()
      }
    }

    if (!disableAutofocusOnNumberKeyPress) {
      window.addEventListener('keydown', isNumberKeyPressed)
    }

    return () => {
      window.removeEventListener('keydown', isNumberKeyPressed)
    }
  }, [autoFocus, disableAutofocusOnNumberKeyPress])

  useEffect(() => {
    if (hasError && inputField.current) {
      inputField.current.focus()
    }
  }, [hasError])

  useEffect(() => {
    if (inputField.current && autoFocus) {
      inputField.current.focus()
    }
  }, [autoFocus])

  useEffect(() => {
    if (hasError) {
      setValue?.(name, '')
    }
  }, [hasError, setValue, name])

  return (
    <div className={className} css={styles.root}>
      <label css={[styles.label, hasError && styles.error]}>
        {children(unicodedValue, greyLength)}

        {control !== undefined ? (
          <Controller
            control={control}
            defaultValue={value}
            name={name}
            render={({
              field: { onChange: handleChange, value: controllerValue },
            }) => (
              <Cleave
                autoComplete="off"
                css={styles.input}
                data-testid="base-code-input"
                htmlRef={(ref) => {
                  inputField.current = ref
                }}
                maxLength={maxLength}
                name={name}
                options={formattedOptions}
                type={type}
                value={controllerValue}
                onChange={handleChange}
                onKeyDown={handleOnKeyDown}
                {...other}
              />
            )}
          />
        ) : (
          <Cleave
            autoComplete="off"
            css={styles.input}
            data-testid="base-code-input"
            htmlRef={(ref) => {
              inputField.current = ref
            }}
            maxLength={maxLength}
            name={name}
            options={formattedOptions}
            type={type}
            value={value}
            onKeyDown={handleOnKeyDown}
            {...other}
          />
        )}
      </label>
      <FormHelperText
        css={styleUtils.margin.top.md}
        errors={fieldErrors}
        isError={hasError}
        name={name}
      >
        {helperText}
      </FormHelperText>
    </div>
  )
}
